import { deleteInventory, putInventory } from "@/common/character/inventory";

export interface InventoryStore {
  armors: Array<ArmorStore>;
  coins: Coins; // This will be handled as items you add with specific names as we have different coin types
  inventory: Array<any>;
  magicItems: Array<any>;
  offhandWeapon: any; // Handled as equip_location
  primaryWeapon: any; // Handled as equip_location
  shields: Array<ArmorStore>;
  weapons: [WeaponStore];
  wornArmor: any; // Handled as equip_location
  wornShield: any; // Handled as equip_location
  cpId: number;
  spId: number;
  gpId: number;
  ppId: number;
}

export interface Coins {
  cp: number;
  sp: number;
  gp: number;
  pp: number;
}

export interface ArmorStore {
  material: string;
  technique: string;
  materialCoverings: Array<string>;
  technicalAddons: Array<string>;
  enchantments: Array<string>;
  passiveGain: any; // This is a big thing in main that needs to get imported
}

export interface WeaponStore {
  weaponType: string;
  combatStyle: string;
  die: string;
  reachRange: string;
  damageType: string;
  strengthRequirement: 0;
  material: string;
  technique: string;
  materialCoverings: Array<string>;
  technicalAddons: Array<string>;
  enchantments: Array<string>;
  passiveGain: any;
}

export default {
  namespaced: true,
  state: (): InventoryStore => ({
    cpId: 0,
    spId: 0,
    gpId: 0,
    ppId: 0,
    armors: [
      {
        material: "none",
        technique: "none",
        materialCoverings: [],
        technicalAddons: [],
        enchantments: [],
        passiveGain: {},
      },
    ],
    coins: {
      cp: 0,
      sp: 0,
      gp: 0,
      pp: 0,
    }, // This will be handled as items you add with specific names as we have different coin types
    inventory: [],
    magicItems: [],
    offhandWeapon: 0, // Handled as equip_location
    primaryWeapon: 0, // Handled as equip_location
    shields: [
      {
        material: "none",
        technique: "none",
        materialCoverings: [],
        technicalAddons: [],
        enchantments: [],
        passiveGain: {},
      },
    ],
    weapons: [
      {
        weaponType: "none",
        combatStyle: "none",
        die: "none",
        reachRange: "none",
        damageType: "none",
        strengthRequirement: 0,
        material: "none",
        technique: "none",
        materialCoverings: [],
        technicalAddons: [],
        enchantments: [],
        passiveGain: {},
      },
    ],
    wornArmor: 0, // Handled as equip_location
    wornShield: 0, // Handled as equip_location
  }),
  getters: {
    armors: (state: InventoryStore) => {
      return state.armors;
    },
    coins: (state: InventoryStore) => {
      return state.coins;
    },
    inventory: (state: InventoryStore) => {
      return state.inventory;
    },
    magicItems: (state: InventoryStore) => {
      return state.magicItems;
    },
    offhandWeapon: (state: InventoryStore) => {
      return state.offhandWeapon;
    },
    primaryWeapon: (state: InventoryStore) => {
      return state.primaryWeapon;
    },
    shields: (state: InventoryStore) => {
      return state.shields;
    },
    weapons: (state: InventoryStore) => {
      return state.weapons;
    },
    wornArmor: (state: InventoryStore) => {
      return state.wornArmor;
    },
    wornShield: (state: InventoryStore) => {
      return state.wornShield;
    },
    getInventory: (state: InventoryStore) => {
      return {
        armors: state.armors,
        coins: state.coins,
        inventory: state.inventory,
        magicItems: state.magicItems,
        offhandWeapon: state.offhandWeapon,
        primaryWeapon: state.primaryWeapon,
        shields: state.shields,
        weapons: state.weapons,
        wornArmor: state.wornArmor,
      };
    },
    cpId: (state: InventoryStore) => {
      return state.cpId;
    },
    spId: (state: InventoryStore) => {
      return state.spId;
    },
    gpId: (state: InventoryStore) => {
      return state.gpId;
    },
    ppId: (state: InventoryStore) => {
      return state.ppId;
    },
  },
  mutations: {
    setArmors: (state: InventoryStore, newData: Array<ArmorStore>) => {
      state.armors = newData;
    },
    setCoin: (state: InventoryStore, newData: Coins) => {
      state.coins = newData;
    },
    setCp: (state: InventoryStore, newData: number) => {
      state.coins.cp = newData;
    },
    setSp: (state: InventoryStore, newData: number) => {
      state.coins.sp = newData;
    },
    setGp: (state: InventoryStore, newData: number) => {
      state.coins.gp = newData;
    },
    setPp: (state: InventoryStore, newData: number) => {
      state.coins.pp = newData;
    },
    setCpId: (state: InventoryStore, newData: number) => {
      state.cpId = newData;
    },
    setSpId: (state: InventoryStore, newData: number) => {
      state.spId = newData;
    },
    setGpId: (state: InventoryStore, newData: number) => {
      state.gpId = newData;
    },
    setPpId: (state: InventoryStore, newData: number) => {
      state.ppId = newData;
    },
    setInventory: (state: InventoryStore, newData: Array<any>) => {
      state.inventory = newData;
    },
    updateInventoryId: (
      state: InventoryStore,
      newData: { id: number; position: number }
    ) => {
      state.inventory[newData.position].id = newData.id;
    },
    addInventory: (state: InventoryStore, newData: any) => {
      let position = -1;
      for (let i = 0; i < state.inventory.length; i++) {
        if (state.inventory[i].itemName === newData.itemName) {
          position = i;
        }
      }
      if (position > -1) {
        state.inventory[position].amount =
          parseInt(state.inventory[position].amount) + parseInt(newData.amount);
        if (state.inventory[position].amount < 1) {
          state.inventory.splice(position, 1);
        }
      } else if (newData.amount >= 1) {
        state.inventory.push(newData);
      }
    },
    removeInventory: (state: InventoryStore, newData: any) => {
      let position = -1;
      for (let i = 0; i < state.inventory.length; i++) {
        if (state.inventory[i].itemName === newData.itemName) {
          position = i;
        }
      }
      if (position > -1) {
        state.inventory.splice(position, 1);
      }
    },
    setMagicItems: (state: InventoryStore, newData: Array<any>) => {
      state.magicItems = newData;
    },
    addMagicItem: (state: InventoryStore, newData: any) => {
      state.magicItems.push(newData);
    },
    updateMagicItemId: (
      state: InventoryStore,
      newData: { name: string; description: string; id: number }
    ) => {
      for (const m of state.magicItems) {
        if (m.name === newData.name && m.description === newData.description) {
          m.id = newData.id;
        }
      }
    },
    deleteMagicItem: (state: InventoryStore, position: number) => {
      state.magicItems.splice(position, 1);
    },
    toggleAttune: (state: InventoryStore, position: number) => {
      state.magicItems[position].attuned = !state.magicItems[position].attuned;
    },
    setOffhandWeapon: (state: InventoryStore, newData: any) => {
      state.offhandWeapon = newData;
    },
    setPrimaryWeapon: (state: InventoryStore, newData: any) => {
      state.primaryWeapon = newData;
    },
    setShields: (state: InventoryStore, newData: Array<ArmorStore>) => {
      state.shields = newData;
    },
    setWeapons: (state: InventoryStore, newData: [WeaponStore]) => {
      state.weapons = newData;
    },
    addShield: (state: InventoryStore, newData: ArmorStore) => {
      state.shields.push(newData);
    },
    removeShield: (state: InventoryStore, pos: number) => {
      state.shields.splice(pos, 1);
    },
    editShield: (state: InventoryStore, newData: any) => {
      state.shields[newData.pos].technique = newData.technique;
      state.shields[newData.pos].material = newData.material;
    },
    addShieldEnchantment: (state: InventoryStore, newData: any) => {
      state.shields[newData.pos].enchantments.push(newData);
    },
    removeShieldEnchantment: (state: InventoryStore, data: any) => {
      for (let i = 0; i < state.shields[data.pos].enchantments.length; i++) {
        if (data.name === state.shields[data.pos].enchantments[i]) {
          state.shields[data.pos].enchantments.splice(i, 1);
          return;
        }
      }
    },
    addShieldTechnicalAddon: (state: InventoryStore, newData: any) => {
      state.shields[newData.pos].technicalAddons.push(newData);
    },
    removeShieldTechnicalAddon: (state: InventoryStore, data: any) => {
      for (let i = 0; i < state.shields[data.pos].technicalAddons.length; i++) {
        if (data.name === state.shields[data.pos].technicalAddons[i]) {
          state.shields[data.pos].technicalAddons.splice(i, 1);
          return;
        }
      }
    },
    addShieldMaterialCovering: (state: InventoryStore, newData: any) => {
      state.shields[newData.pos].materialCoverings.push(newData);
    },
    removeShieldMaterialCovering: (state: InventoryStore, data: any) => {
      for (
        let i = 0;
        i < state.shields[data.pos].materialCoverings.length;
        i++
      ) {
        if (data.name === state.shields[data.pos].materialCoverings[i]) {
          state.shields[data.pos].materialCoverings.splice(i, 1);
          return;
        }
      }
    },
    addArmor: (state: InventoryStore, newData: ArmorStore) => {
      state.armors.push(newData);
    },
    removeArmor: (state: InventoryStore, pos: number) => {
      state.armors.splice(pos, 1);
    },
    editArmor: (state: InventoryStore, newData: any) => {
      state.armors[newData.pos].technique = newData.technique;
      state.armors[newData.pos].material = newData.material;
    },
    addArmorEnchantment: (state: InventoryStore, newData: any) => {
      state.armors[newData.pos].enchantments.push(newData);
    },
    removeArmorEnchantment: (state: InventoryStore, data: any) => {
      for (let i = 0; i < state.armors[data.pos].enchantments.length; i++) {
        if (data.name === state.armors[data.pos].enchantments[i]) {
          state.armors[data.pos].enchantments.splice(i, 1);
          return;
        }
      }
    },
    addArmorTechnicalAddon: (state: InventoryStore, newData: any) => {
      state.armors[newData.pos].technicalAddons.push(newData);
    },
    removeArmorTechnicalAddon: (state: InventoryStore, data: any) => {
      for (let i = 0; i < state.armors[data.pos].technicalAddons.length; i++) {
        if (data.name === state.armors[data.pos].technicalAddons[i]) {
          state.armors[data.pos].technicalAddons.splice(i, 1);
          return;
        }
      }
    },
    addArmorMaterialCovering: (state: InventoryStore, newData: any) => {
      state.armors[newData.pos].materialCoverings.push(newData);
    },
    removeArmorMaterialCovering: (state: InventoryStore, data: any) => {
      for (
        let i = 0;
        i < state.armors[data.pos].materialCoverings.length;
        i++
      ) {
        if (data.name === state.armors[data.pos].materialCoverings[i]) {
          state.armors[data.pos].materialCoverings.splice(i, 1);
          return;
        }
      }
    },
    addWeapon: (state: InventoryStore, newData: WeaponStore) => {
      state.weapons.push(newData);
    },
    removeWeapon: (state: InventoryStore, pos: number) => {
      state.weapons.splice(pos, 1);
    },
    editWeapon: (state: InventoryStore, newData: any) => {
      state.weapons[newData.pos].technique = newData.technique;
      state.weapons[newData.pos].material = newData.material;
      state.weapons[newData.pos].combatStyle = newData.combatStyle;
      state.weapons[newData.pos].weaponType = newData.weaponType;
      state.weapons[newData.pos].reachRange = newData.reachRange;
      state.weapons[newData.pos].damageType = newData.damageType;
      state.weapons[newData.pos].strengthRequirement =
        newData.strengthRequirement;
      state.weapons[newData.pos].die = newData.die;
    },
    addEnchantment: (state: InventoryStore, newData: any) => {
      state.weapons[newData.pos].enchantments.push(newData);
    },
    removeEnchantment: (state: InventoryStore, data: any) => {
      for (let i = 0; i < state.weapons[data.pos].enchantments.length; i++) {
        if (data.name === state.weapons[data.pos].enchantments[i]) {
          state.weapons[data.pos].enchantments.splice(i, 1);
          return;
        }
      }
    },
    addMaterialCovering: (state: InventoryStore, newData: any) => {
      state.weapons[newData.pos].materialCoverings.push(newData);
    },
    removeMaterialCovering: (state: InventoryStore, data: any) => {
      for (
        let i = 0;
        i < state.weapons[data.pos].materialCoverings.length;
        i++
      ) {
        if (data.name === state.weapons[data.pos].materialCoverings[i]) {
          state.weapons[data.pos].materialCoverings.splice(i, 1);
          return;
        }
      }
    },
    addTechnicalAddon: (state: InventoryStore, newData: any) => {
      state.weapons[newData.pos].technicalAddons.push(newData);
    },
    removeTechnicalAddon: (state: InventoryStore, data: any) => {
      for (let i = 0; i < state.weapons[data.pos].technicalAddons.length; i++) {
        if (data.name === state.weapons[data.pos].technicalAddons[i]) {
          state.weapons[data.pos].technicalAddons.splice(i, 1);
          return;
        }
      }
    },
    setWornArmor: (state: InventoryStore, newData: null) => {
      state.wornArmor = newData;
    },
    setWornShield: (state: InventoryStore, newData: null) => {
      state.wornShield = newData;
    },
  },
  actions: {
    toggleAttuneDb: (
      context: any,
      newData: { position: number; character_id: string }
    ) => {
      const attuned = !context.getters.magicItems[newData.position].attuned;
      context.commit("toggleAttune", newData.position);
      if (attuned) {
        context.dispatch("updateInventory", {
          character_id: newData.character_id,
          isMagic: true,
          name: context.getters.magicItems[newData.position].itemName,
          description: context.getters.magicItems[newData.position].description,
          count: 2,
        });
      } else {
        context.dispatch("updateInventory", {
          character_id: newData.character_id,
          isMagic: true,
          name: context.getters.magicItems[newData.position].itemName,
          description: context.getters.magicItems[newData.position].description,
          count: 1,
        });
      }
    },
    updateCp: (
      context: any,
      newData: { character_id: string; amount: number }
    ) => {
      context.commit("setCp", newData.amount);
      context.dispatch("updateMundaneItem", {
        character_id: newData.character_id,
        isMagic: false,
        name: "CP",
        description: "HIDDEN$",
        count: newData.amount,
        id: context.getters.cpId,
      });
    },
    updateSp: (
      context: any,
      newData: { character_id: string; amount: number }
    ) => {
      context.commit("setSp", newData.amount);
      context.dispatch("updateMundaneItem", {
        character_id: newData.character_id,
        isMagic: false,
        name: "SP",
        description: "HIDDEN$",
        count: newData.amount,
        id: context.getters.spId,
      });
    },
    updateGp: (
      context: any,
      newData: { character_id: string; amount: number }
    ) => {
      context.commit("setGp", newData.amount);
      context.dispatch("updateMundaneItem", {
        character_id: newData.character_id,
        isMagic: false,
        name: "GP",
        description: "HIDDEN$",
        count: newData.amount,
        id: context.getters.gpId,
      });
    },
    updatePp: (
      context: any,
      newData: { character_id: string; amount: number }
    ) => {
      context.commit("setPp", newData.amount);
      context.dispatch("updateMundaneItem", {
        character_id: newData.character_id,
        isMagic: false,
        name: "PP",
        description: "HIDDEN$",
        count: newData.amount,
        id: context.getters.ppId,
      });
    },
    updateMundaneItem: (
      context: any,
      newData: {
        character_id: string;
        isMagic: boolean;
        name: string;
        description: string;
        count: number;
        id: number;
      }
    ) => {
      let position = -1;
      for (let i = 0; i < context.getters.inventory.length; i++) {
        if (context.getters.inventory[i].itemName === newData.name) {
          position = i;
        }
      }
      if (
        position > -1 &&
        newData.name != "CP" &&
        newData.name != "SP" &&
        newData.name != "GP" &&
        newData.name != "PP"
      ) {
        const finalCount =
          context.getters.inventory[position].amount + newData.count;
        context.dispatch("updateInventory", {
          character_id: newData.character_id,
          isMagic: newData.isMagic,
          name: newData.name,
          description: context.getters.inventory[position].description,
          count: finalCount,
          id: newData.id,
          position: position,
        });
        context.getters.inventory[position].amount = finalCount;
        if (finalCount < 1) {
          context.getters.inventory.splice(position, 1);
        }
      } else if (
        newData.count >= 1 &&
        newData.name != "CP" &&
        newData.name != "SP" &&
        newData.name != "GP" &&
        newData.name != "PP"
      ) {
        const push = {
          isMagic: newData.isMagic,
          itemName: newData.name,
          description: newData.description,
          amount: newData.count,
        };
        context.getters.inventory.push(push);
        const loc = context.getters.inventory.length - 1;
        context.dispatch("updateInventory", {
          character_id: newData.character_id,
          isMagic: newData.isMagic,
          name: newData.name,
          description: newData.description,
          count: newData.count,
          id: newData.id,
          position: loc,
        });
      } else if (
        newData.name == "CP" ||
        newData.name == "SP" ||
        newData.name == "GP" ||
        newData.name == "PP"
      ) {
        let count = newData.count;
        if (count < 0) {
          count = 0;
        }
        context.dispatch("updateInventory", {
          character_id: newData.character_id,
          isMagic: newData.isMagic,
          name: newData.name,
          description: newData.description,
          count: count,
          id: newData.id,
          position: 0,
        });
      }
    },
    updateInventory: (
      context: any,
      newData: {
        character_id: string;
        isMagic: boolean;
        name: string;
        description: string;
        count: number;
        id: number;
        position: number;
      }
    ) => {
      if (newData.count < 1) {
        deleteInventory(
          newData.character_id,
          newData.name,
          newData.count,
          newData.description,
          newData.isMagic,
          newData.id
        );
      } else {
        putInventory(
          newData.character_id,
          newData.name,
          newData.count,
          newData.description,
          newData.isMagic,
          newData.id
        ).then((response) => {
          if (newData.name === "CP") {
            context.commit("setCpId", response.data[0].id);
          } else if (newData.name === "SP") {
            context.commit("setSpId", response.data[0].id);
          } else if (newData.name === "GP") {
            context.commit("setGpId", response.data[0].id);
          } else if (newData.name === "PP") {
            context.commit("setPpId", response.data[0].id);
          } else if (newData.isMagic) {
            context.commit("updateMagicItemId", {
              name: newData.name,
              description: newData.description,
              id: response.data[0].id,
            });
          } else {
            context.commit("updateInventoryId", {
              id: response.data[0].id,
              position: newData.position,
            });
          }
        });
      }
    },
  },
};
